import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  AlertOctagon,
  AlertTriangle,
  Info,
  CheckCircle,
  Database,
  Zap,
  Activity,
  TrendingDown,
  Clock,
  ShoppingBag,
  Server,
  ChevronRight,
} from 'lucide-react';
import { apiClient, type Anomaly } from '../lib/api.js';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card.js';
import { Badge } from '../components/ui/Badge.js';
import { cn, formatBRL, formatDateTime, formatRelative } from '../lib/utils.js';

const severityConfig: Record<
  string,
  { label: string; color: string; bgColor: string; icon: typeof AlertOctagon }
> = {
  critical: {
    label: 'CRÍTICO',
    color: 'text-rose-300',
    bgColor: 'bg-rose-500/15 border-rose-500/40',
    icon: AlertOctagon,
  },
  high: {
    label: 'ALTO',
    color: 'text-orange-300',
    bgColor: 'bg-orange-500/15 border-orange-500/40',
    icon: AlertTriangle,
  },
  warning: {
    label: 'ATENÇÃO',
    color: 'text-amber-300',
    bgColor: 'bg-amber-500/15 border-amber-500/30',
    icon: AlertTriangle,
  },
  info: {
    label: 'INFO',
    color: 'text-blue-300',
    bgColor: 'bg-blue-500/15 border-blue-500/30',
    icon: Info,
  },
};

const statusBadgeColor: Record<string, string> = {
  open: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
  acknowledged: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  resolved: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
};

function detectorLabel(detector: string): string {
  const map: Record<string, string> = {
    roas_threshold: 'ROAS Threshold',
    cost_spike: 'Pico de custo',
    refund_spike: 'Pico de reembolso',
    conversion_drop: 'Queda de conversão',
    sla_breach: 'SLA breach',
    saas_cost_increase: 'Custo SaaS',
    ledger_balance: 'Ledger desbalanceado',
  };
  return map[detector] ?? detector;
}

function AnomalyCard({
  a,
  onAck,
  onResolve,
}: {
  a: Anomaly;
  onAck: (id: string) => void;
  onResolve: (id: string) => void;
}): JSX.Element {
  const cfg = severityConfig[a.severity] ?? severityConfig.warning;
  const Icon = cfg.icon;
  const impact = Number(a.estimatedImpactCents ?? 0);
  const isClosed = a.status === 'resolved';

  return (
    <Card className={cn('overflow-hidden', isClosed && 'opacity-60')}>
      <div className={cn('border-l-4 px-4 py-3', cfg.bgColor)}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <Icon className={cn('h-5 w-5 mt-0.5 shrink-0', cfg.color)} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className={cn('text-[10px] font-bold tracking-wider', cfg.color)}>
                  {cfg.label}
                </span>
                <span className="text-[10px] text-muted-foreground">·</span>
                <span className="text-[10px] text-muted-foreground">
                  {detectorLabel(a.detector)}
                </span>
                <span className="text-[10px] text-muted-foreground">·</span>
                <span className="text-[10px] text-muted-foreground">
                  {formatRelative(a.detectedAt)}
                </span>
              </div>
              <div className="text-sm font-medium leading-snug">{a.description}</div>
            </div>
          </div>
          <span
            className={cn(
              'shrink-0 inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium border tabular-nums',
              statusBadgeColor[a.status] ?? statusBadgeColor.open,
            )}
          >
            {a.status}
          </span>
        </div>
      </div>

      <CardContent className="p-4 space-y-3">
        {a.rootCauseAnalysis && (
          <div>
            <div className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">
              Análise da causa
            </div>
            <div className="text-xs text-muted-foreground leading-relaxed">
              {a.rootCauseAnalysis}
            </div>
          </div>
        )}

        {a.suggestedAction && (
          <div className="rounded-md bg-primary/5 border border-primary/20 px-3 py-2">
            <div className="text-[10px] text-primary uppercase tracking-wide mb-0.5">
              Ação sugerida
            </div>
            <div className="text-xs">{a.suggestedAction}</div>
          </div>
        )}

        <div className="flex items-center justify-between text-xs flex-wrap gap-2">
          {impact > 0 && (
            <div className="flex items-center gap-1 text-rose-400">
              <TrendingDown className="h-3.5 w-3.5" />
              <span className="tabular-nums">
                Impacto: <strong>{formatBRL(impact)}</strong>
              </span>
            </div>
          )}
          {Object.keys(a.dimensionKeys ?? {}).length > 0 && (
            <div className="flex flex-wrap gap-1">
              {Object.entries(a.dimensionKeys).slice(0, 4).map(([k, v]) => (
                <Badge key={k} variant="outline" className="text-[9px]">
                  {k}: {String(v)}
                </Badge>
              ))}
            </div>
          )}
        </div>

        {a.status !== 'resolved' && (
          <div className="flex gap-1.5 pt-1 border-t border-border/40">
            {a.status === 'open' && (
              <button
                onClick={() => onAck(a.id)}
                className="flex-1 inline-flex items-center justify-center gap-1 rounded-md bg-amber-500/15 text-amber-300 hover:bg-amber-500/25 px-2 py-1.5 text-xs font-medium border border-amber-500/30"
              >
                Reconhecer
              </button>
            )}
            <button
              onClick={() => onResolve(a.id)}
              className="flex-1 inline-flex items-center justify-center gap-1 rounded-md bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 px-2 py-1.5 text-xs font-medium border border-emerald-500/30"
            >
              <CheckCircle className="h-3.5 w-3.5" />
              Resolver
            </button>
          </div>
        )}

        {a.resolvedAt && (
          <div className="text-[10px] text-muted-foreground border-t border-border/40 pt-2">
            Resolvido {formatRelative(a.resolvedAt)}
            {a.resolvedByName && ` por ${a.resolvedByName}`}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function Operacoes(): JSX.Element {
  const qc = useQueryClient();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['ops-overview'],
    queryFn: () => apiClient.opsOverview(),
    refetchInterval: 60000, // 1min
  });

  const ackMutation = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'acknowledge' | 'resolve' }) =>
      apiClient.ackAnomaly(id, action),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ops-overview'] }),
  });

  if (isLoading) {
    return <div className="p-8 text-muted-foreground">Carregando operações...</div>;
  }

  if (isError || !data) {
    return <div className="p-8 text-rose-400">Erro: {String(error)}</div>;
  }

  const { anomalies, health, slaBreaches, todayStats, openByCriticality } = data;
  const openAnomalies = anomalies.filter((a) => a.status === 'open');
  const ackAnomalies = anomalies.filter((a) => a.status === 'acknowledged');
  const resolvedAnomalies = anomalies.filter((a) => a.status === 'resolved');

  return (
    <div className="p-6 space-y-6 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Activity className="h-6 w-6 text-primary" />
            Operações & Saúde
          </h1>
          <p className="text-sm text-muted-foreground">
            Anomaly detection · SLA breaches · Health checks · Refresh 60s
          </p>
        </div>
      </div>

      {/* Top row: anomalies by severity */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {(['critical', 'high', 'warning', 'info'] as const).map((sev) => {
          const cfg = severityConfig[sev];
          const Icon = cfg.icon;
          const count = openByCriticality[sev];
          return (
            <Card key={sev} className={cn('border', count > 0 && cfg.bgColor)}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-1">
                  <span className={cn('text-[10px] font-bold tracking-wider', cfg.color)}>
                    {cfg.label}
                  </span>
                  <Icon className={cn('h-4 w-4', cfg.color)} />
                </div>
                <div className="text-2xl font-bold tabular-nums">{count}</div>
                <div className="text-[10px] text-muted-foreground mt-1">
                  abertas
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Health + SLA + Today */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Health */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Server className="h-4 w-4" /> Health checks
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Database className="h-3.5 w-3.5 text-muted-foreground" />
                <span>Postgres</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground tabular-nums">
                  {health.db.latencyMs}ms
                </span>
                <span
                  className={cn(
                    'inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium',
                    health.db.ok
                      ? 'bg-emerald-500/15 text-emerald-300'
                      : 'bg-rose-500/15 text-rose-300',
                  )}
                >
                  {health.db.ok ? '● online' : '● erro'}
                </span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Zap className="h-3.5 w-3.5 text-muted-foreground" />
                <span>Redis</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground tabular-nums">
                  {health.redis.latencyMs}ms
                </span>
                <span
                  className={cn(
                    'inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium',
                    health.redis.ok
                      ? 'bg-emerald-500/15 text-emerald-300'
                      : 'bg-rose-500/15 text-rose-300',
                  )}
                >
                  {health.redis.ok ? '● online' : '● erro'}
                </span>
              </div>
            </div>
            <div className="border-t border-border/40 pt-3 space-y-2 text-xs text-muted-foreground">
              <div className="flex items-center justify-between">
                <span>Último evento</span>
                <span className="tabular-nums">
                  {health.lastEventAt ? formatRelative(health.lastEventAt) : '—'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span>Último webhook Ticto</span>
                <span className="tabular-nums">
                  {health.lastTictoWebhookAt
                    ? formatRelative(health.lastTictoWebhookAt)
                    : '—'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span>Última venda</span>
                <span className="tabular-nums">
                  {health.lastOrderAt ? formatRelative(health.lastOrderAt) : '—'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span>Última mensagem</span>
                <span className="tabular-nums">
                  {health.lastMessageAt ? formatRelative(health.lastMessageAt) : '—'}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* SLA breaches */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-4 w-4" /> SLA breaches
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <Link
              to="/inbox"
              className="flex items-center justify-between hover:bg-muted/30 -mx-2 px-2 py-1 rounded"
            >
              <div>
                <div>Tickets — 1ª resposta</div>
                <div className="text-[10px] text-muted-foreground">
                  high/urgent sem resposta no SLA
                </div>
              </div>
              <span
                className={cn(
                  'inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold tabular-nums',
                  slaBreaches.ticketsFirstResponse > 0
                    ? 'bg-rose-500/20 text-rose-300'
                    : 'bg-emerald-500/15 text-emerald-300',
                )}
              >
                {slaBreaches.ticketsFirstResponse}
              </span>
            </Link>
            <div className="flex items-center justify-between">
              <div>
                <div>Tickets — resolução</div>
                <div className="text-[10px] text-muted-foreground">
                  SLA de resolução expirado
                </div>
              </div>
              <span
                className={cn(
                  'inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold tabular-nums',
                  slaBreaches.ticketsResolution > 0
                    ? 'bg-amber-500/20 text-amber-300'
                    : 'bg-emerald-500/15 text-emerald-300',
                )}
              >
                {slaBreaches.ticketsResolution}
              </span>
            </div>
            <Link
              to="/inbox"
              className="flex items-center justify-between hover:bg-muted/30 -mx-2 px-2 py-1 rounded"
            >
              <div>
                <div>Conversas sem resposta &gt;2h</div>
                <div className="text-[10px] text-muted-foreground">
                  primeira resposta pendente
                </div>
              </div>
              <span
                className={cn(
                  'inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold tabular-nums',
                  slaBreaches.conversationsNoResponse2h > 0
                    ? 'bg-amber-500/20 text-amber-300'
                    : 'bg-emerald-500/15 text-emerald-300',
                )}
              >
                {slaBreaches.conversationsNoResponse2h}
              </span>
            </Link>
            <Link
              to="/recuperacao"
              className="flex items-center justify-between hover:bg-muted/30 -mx-2 px-2 py-1 rounded"
            >
              <div>
                <div>Recovery stale &gt;30min</div>
                <div className="text-[10px] text-muted-foreground">
                  {slaBreaches.recoveryQueueSize} total na fila
                </div>
              </div>
              <span
                className={cn(
                  'inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold tabular-nums',
                  slaBreaches.recoveryStale30min > 0
                    ? 'bg-rose-500/20 text-rose-300'
                    : 'bg-emerald-500/15 text-emerald-300',
                )}
              >
                {slaBreaches.recoveryStale30min}
              </span>
            </Link>
          </CardContent>
        </Card>

        {/* Today snapshot */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShoppingBag className="h-4 w-4" /> Hoje
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <div className="text-[10px] text-muted-foreground uppercase tracking-wide">
                Vendas
              </div>
              <div className="text-2xl font-bold tabular-nums text-emerald-400">
                {todayStats.salesToday}
              </div>
            </div>
            <div>
              <div className="text-[10px] text-muted-foreground uppercase tracking-wide">
                Receita
              </div>
              <div className="text-xl font-bold tabular-nums">
                {formatBRL(todayStats.revenueTodayCents)}
              </div>
            </div>
            <div>
              <div className="text-[10px] text-muted-foreground uppercase tracking-wide">
                Eventos
              </div>
              <div className="text-base font-semibold tabular-nums">
                {todayStats.eventsToday}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Anomalies feed */}
      {openAnomalies.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <AlertOctagon className="h-5 w-5 text-rose-400" />
            Anomalias abertas ({openAnomalies.length})
          </h2>
          <div className="grid lg:grid-cols-2 gap-4">
            {openAnomalies.map((a) => (
              <AnomalyCard
                key={a.id}
                a={a}
                onAck={(id) => ackMutation.mutate({ id, action: 'acknowledge' })}
                onResolve={(id) => ackMutation.mutate({ id, action: 'resolve' })}
              />
            ))}
          </div>
        </div>
      )}

      {ackAnomalies.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-base font-semibold flex items-center gap-2 text-muted-foreground">
            <AlertTriangle className="h-4 w-4 text-amber-400" />
            Reconhecidas ({ackAnomalies.length})
          </h2>
          <div className="grid lg:grid-cols-2 gap-4">
            {ackAnomalies.map((a) => (
              <AnomalyCard
                key={a.id}
                a={a}
                onAck={(id) => ackMutation.mutate({ id, action: 'acknowledge' })}
                onResolve={(id) => ackMutation.mutate({ id, action: 'resolve' })}
              />
            ))}
          </div>
        </div>
      )}

      {resolvedAnomalies.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-base font-semibold flex items-center gap-2 text-muted-foreground">
            <CheckCircle className="h-4 w-4 text-emerald-400" />
            Resolvidas ({resolvedAnomalies.length})
          </h2>
          <div className="grid lg:grid-cols-2 gap-4">
            {resolvedAnomalies.map((a) => (
              <AnomalyCard
                key={a.id}
                a={a}
                onAck={(id) => ackMutation.mutate({ id, action: 'acknowledge' })}
                onResolve={(id) => ackMutation.mutate({ id, action: 'resolve' })}
              />
            ))}
          </div>
        </div>
      )}

      {anomalies.length === 0 && (
        <Card>
          <CardContent className="p-12 text-center">
            <CheckCircle className="h-12 w-12 mx-auto text-emerald-400 mb-3" />
            <div className="text-lg font-semibold mb-1">Tudo verde 🎉</div>
            <div className="text-sm text-muted-foreground">
              Nenhuma anomalia detectada.
            </div>
          </CardContent>
        </Card>
      )}

      <div className="text-xs text-muted-foreground border-t border-border pt-4">
        <strong>Detectores ativos:</strong> ROAS threshold · cost_spike · refund_spike ·
        conversion_drop · sla_breach · saas_cost_increase · ledger_balance. Em produção, detectores
        rodam em workers Temporal com schedule + push notification para urgent/critical.
      </div>
    </div>
  );
}
